import React, { useState, useEffect, useRef } from 'react';
import { Menu, Sidebar } from '@metaicg/sensei-ui';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import Profile from '@/shared/components/layout/components/profile';
import Logo from '@/shared/components/logo/logo';
import { mainNavigation, MenuItems } from '@/routes/navigation';
import CollapseButton from '@/shared/components/layout/components/sidebar/collapse-button';
import AppVersion from '@/shared/components/app-version';
import { SidebarProps } from '@/shared/components/layout/components/sidebar/sidebar.types';
import styles from '@/shared/components/layout/components/sidebar/styles';
import { ROUTE_PATHS } from '@/routes';
import useStorage from '@/shared/hooks/use-storage';

const SidebarStyled = styled(Sidebar)<SidebarProps>(() => styles.container);
const BottomContainer = styled.div(styles.bottomContainer);

const LayoutSidebar = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { store } = useStorage();
  const [menuCollapsed, setMenuCollapsed] = useState(false);
  const [mainNavigationFilteredByPermission, setMainNavigationFilteredByPermission] = useState<MenuItems | null>(null);

  const storeRef = useRef(store);

  useEffect(() => {
    storeRef.current.getAuth().then((auth) => {
      const userRoles = auth?.authorities?.map((role) => role.id);
      if (userRoles) {
        const filtered = mainNavigation.filter(
          (item) =>
            !item.accessRoles ||
            item.accessRoles.length === 0 ||
            item.accessRoles.some((role) => userRoles.includes(role))
        );
        setMainNavigationFilteredByPermission(filtered);
      }
    });
  }, []);

  const translateMenu = (menuItems: MenuItems): MenuItems =>
    menuItems?.map((item) => {
      const translatedItem = {
        ...item,
        label: t(item.label as string),
      };

      if (item.children) {
        translatedItem.children = translateMenu(item.children);
      }

      return translatedItem;
    });

  const handleOnCollapse = (collapsed: boolean) => {
    setMenuCollapsed(collapsed);
  };

  const handleOnContainerClick = (e: any) => {
    e.stopPropagation();

    if (e.target === e.currentTarget || e.target?.tagName === 'NAV') {
      setMenuCollapsed(!menuCollapsed);
    }
  };

  return (
    <SidebarStyled
      navigationAlignment="top"
      topContainer={<Logo />}
      bottomContainer={
        <>
          <BottomContainer>
            <CollapseButton collapsed={menuCollapsed} onClick={handleOnCollapse} />

            <Profile collapsed={menuCollapsed} />
          </BottomContainer>
          <AppVersion showSha collapsed={menuCollapsed} />
        </>
      }
      width={menuCollapsed ? 80 : 280}
      collapsedWidth={menuCollapsed ? 80 : 280}
      collapsed={menuCollapsed}
    >
      <div onClick={handleOnContainerClick}>
        {mainNavigationFilteredByPermission && (
          <Menu
            defaultSelectedKeys={[location.pathname.replace(/\/$/, '') || '/']}
            defaultOpenKeys={[location.pathname.replace(/\/$/, '') || '/', ROUTE_PATHS.root]}
            selectedKeys={[location.pathname]}
            mode="inline"
            themeMode="dark"
            inlineCollapsed={menuCollapsed}
            onSelect={({ key }) => navigate(key)}
            items={translateMenu(mainNavigationFilteredByPermission)}
          />
        )}
      </div>
    </SidebarStyled>
  );
};

export default LayoutSidebar;
